import type { ImportResult } from '@integratio/core';
import { AlertCircle, CheckCircle2, FileSpreadsheet } from 'lucide-react';
import { formatBytes } from '../lib/format';

export function FileValidationSummary({ result }: { result: ImportResult }): JSX.Element {
  const blocked = result.missingColumns.length > 0 || result.stakeholders.length === 0;

  return (
    <section className="card p-6">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-mist">
            <FileSpreadsheet className="h-5 w-5 text-brand-slate" aria-hidden />
          </span>
          <div>
            <p className="eyebrow">Arquivo importado</p>
            <p className="text-sm font-semibold text-brand-ink">{result.fileName}</p>
            <p className="text-xs text-brand-slate">
              {formatBytes(result.fileSize)} · aba “{result.sheetName}” · cabeçalho na linha {result.headerRowNumber}
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-10 gap-y-3 sm:grid-cols-4">
          <div>
            <dt className="eyebrow">Linhas lidas</dt>
            <dd className="stat-value">{result.totalRows}</dd>
          </div>
          <div>
            <dt className="eyebrow">Stakeholders válidos</dt>
            <dd className="stat-value">{result.stakeholders.length}</dd>
          </div>
          <div>
            <dt className="eyebrow">Linhas ignoradas</dt>
            <dd className="stat-value">{result.ignoredRows}</dd>
          </div>
          <div>
            <dt className="eyebrow">Alertas</dt>
            <dd className={['stat-value', result.warnings.length > 0 ? 'text-state-attention' : ''].join(' ')}>
              {result.warnings.length}
            </dd>
          </div>
        </dl>
      </div>

      {blocked ? (
        <div
          role="alert"
          className="mt-6 rounded-xl border border-brand-red/30 bg-brand-red/5 p-4 text-sm text-brand-redDark"
        >
          <p className="flex items-center gap-2 font-semibold">
            <AlertCircle className="h-4 w-4" aria-hidden />
            Não é possível continuar com esta planilha
          </p>
          {result.missingColumns.length > 0 && (
            <>
              <p className="mt-2">Colunas obrigatórias ausentes:</p>
              <ul className="mt-1 list-inside list-disc">
                {result.missingColumns.map((column) => (
                  <li key={column}>{column}</li>
                ))}
              </ul>
              <p className="mt-2">
                Corrija os títulos das colunas na planilha original e envie o arquivo novamente. Os nomes devem estar na
                mesma linha de cabeçalho.
              </p>
            </>
          )}
          {result.stakeholders.length === 0 && (
            <p className="mt-2">Nenhum stakeholder válido foi encontrado. Verifique se a coluna “Stakeholder” está preenchida.</p>
          )}
        </div>
      ) : (
        <p className="mt-6 flex items-center gap-2 rounded-xl border border-state-positive/25 bg-state-positive/5 px-4 py-3 text-sm text-state-positive">
          <CheckCircle2 className="h-4 w-4" aria-hidden />
          Todas as colunas obrigatórias foram encontradas. Confira os dados antes de gerar os gráficos.
        </p>
      )}

      {result.unmappedHeaders.length > 0 && (
        <p className="mt-3 text-xs text-brand-slate">
          Colunas adicionais ignoradas no processamento: {result.unmappedHeaders.join(' · ')}
        </p>
      )}
    </section>
  );
}
